'use client'

// Hero live pane — the walking list + one-line reasoning strip on the right of
// the hero. Ported from apps/frontend/design/legix-website-v2/Website.dc.html
// (HERO PANE block of the data-dc-script + the `.lgx-heropane` template).
//
// A second live list, driven separately from the Tasks section on purpose. The
// two are never on screen together, but they must not SHARE state: the tasks
// driver starts on scroll-in and plays once, and if the hero borrowed it the
// hero would sit idle until you scrolled — or, started early, would have spent
// the tasks reveal before you arrived. So this is its own walk over the same
// window.SEED rows: posted-only, looping, no reasoning, no interaction.

import { Component } from 'react'
import {
  type Seed,
  type SeedTxn,
  type StreamEntry,
  type PaneMetrics,
  Timers,
  acctName,
  paneMetrics,
  prefersReduced,
  rparts,
  scopeLabel,
  seed,
  streamEntries,
  taskMem,
  trimReason,
} from './landing-live'

const HERO_ROW_MS = 3000

interface HeroPaneState {
  heroTid: string | null // hero pane: its own current row…
  heroAt: number // …how far its walk has reached (flags land in step, not up front)…
  heroPosted: string[] // …and its own posted set. Never shared with the tasks driver.
}

interface HeroRow {
  id: string
  cls: string
  date: string
  raw: string
  src: string
  chip: string
  amt: string
}

export class HeroPane extends Component<Record<string, never>, HeroPaneState> {
  override state: HeroPaneState = { heroTid: null, heroAt: -1, heroPosted: [] }

  private timers = new Timers()
  private metrics: Record<string, PaneMetrics> = {}
  private heroPosted: string[] = []
  private heroListCache: SeedTxn[] | null = null
  private heroOn = false
  private reduced = false
  private onResize: (() => void) | null = null
  private rz: ReturnType<typeof setTimeout> | undefined

  override componentDidMount(): void {
    this.reduced = prefersReduced()
    this.heroBegin() // the hero is on screen at load; it does not wait for a scroll
    // The track is computed during render, so crossing the 980px breakpoint would
    // otherwise leave it on offsets measured against the old box height.
    this.onResize = () => {
      clearTimeout(this.rz)
      this.rz = setTimeout(() => this.forceUpdate(), 120)
    }
    window.addEventListener('resize', this.onResize)
  }

  override componentWillUnmount(): void {
    this.timers.clear()
    if (this.onResize) window.removeEventListener('resize', this.onResize)
    clearTimeout(this.rz)
  }

  private at(ms: number, fn: () => void): void {
    this.timers.at(ms, fn)
  }

  private heroBegin(): void {
    if (this.heroOn) return
    if (!seed()) {
      this.at(60, () => this.heroBegin())
      return
    }
    this.heroOn = true
    this.heroPosted = []
    this.at(this.reduced ? 0 : 700, () => (this.reduced ? this.heroRest() : this.heroStep(0)))
  }

  private heroList(): SeedTxn[] {
    if (this.heroListCache) return this.heroListCache
    const D = seed()
    this.heroListCache = (D ? D.transactions.slice() : []).sort((a, b) =>
      a.date < b.date ? -1 : a.date > b.date ? 1 : a.id < b.id ? -1 : 1,
    )
    return this.heroListCache
  }

  private heroStep(i: number): void {
    const list = this.heroList()
    if (i >= list.length) {
      this.heroRest()
      return
    }
    const t = list[i]
    // flagged rows are passed over, the same way they are in the tasks section:
    // they stay for a person
    if (t.state !== 'needsyou' && this.heroPosted.indexOf(t.id) === -1) this.heroPosted.push(t.id)
    // heroAt is how far the walk has got. A flagged row is only marked once the
    // walk REACHES it — otherwise every flag in the list lights up the moment
    // the first row posts, giving away the outcome before the pass has run.
    this.setState({ heroTid: t.id, heroAt: i, heroPosted: this.heroPosted.slice() })
    // Paced to the STRIP, not the list: the reasoning line has to be readable
    // before the walk moves off the row. Rows that share an entry hold the same
    // line, so a run of them reads as one thought rather than a flicker.
    this.at(HERO_ROW_MS, () => this.heroStep(i + 1))
  }

  // Hold on the finished list, then start over — the hero has no end state to arrive at.
  private heroRest(): void {
    if (this.reduced) {
      this.heroPosted = this.heroList()
        .filter((t) => t.state !== 'needsyou')
        .map((t) => t.id)
      this.setState({
        heroTid: null,
        heroAt: this.heroList().length,
        heroPosted: this.heroPosted.slice(),
      })
      return
    }
    this.setState({ heroTid: null })
    this.at(2600, () => {
      this.heroPosted = []
      this.setState({ heroPosted: [], heroTid: null, heroAt: -1 })
      this.at(700, () => this.heroStep(0))
    })
  }

  private txn(id: string): SeedTxn | null {
    const D = seed()
    return D ? (D.transactions.find((t) => t.id === id) ?? null) : null
  }

  override render() {
    const S = this.state
    const D = seed()

    let heroRows: HeroRow[] = []
    let heroShift = 0
    let heroDone = 0
    let heroCount = ''
    let heroFlagCount = ''
    let heroLabel = 'Ready'
    let heroSubj = ''
    let heroReason = ''
    let heroQuote = ''
    let heroTail = ''
    let heroCite = ''

    if (D) {
      const heroList = this.heroList()
      heroRows = heroList.map((t, i) => {
        const isFlag = t.state === 'needsyou' && i <= S.heroAt
        const isDone = S.heroPosted.indexOf(t.id) > -1
        let cls = 'ts-row'
        let chip = ''
        if (isDone) {
          cls += ' is-handled'
          chip = t.split ? 'Split across 2 accounts' : acctName(D, t.correctedTo || t.to || '')
        } else if (isFlag) {
          cls += ' is-flag'
          chip = t.reason || 'Needs you'
        } else cls += ' is-pending'
        if (S.heroTid === t.id) cls += ' is-current'
        return { id: t.id, cls, date: t.date, raw: t.raw, src: t.from, chip, amt: D.money(t.amt) }
      })

      // same track rule as the tasks pane: the current row parks ~42% down the box
      const hm = paneMetrics(this.metrics, '.lgx-heropane .ts-rows')
      if (hm && S.heroTid) {
        let hi = -1
        for (let k = 0; k < heroList.length; k++)
          if (heroList[k].id === S.heroTid) {
            hi = k
            break
          }
        if (hi > -1)
          heroShift = Math.max(
            0,
            Math.min(heroList.length * hm.row - hm.box, hi * hm.row - hm.box * 0.42),
          )
      }
      heroDone = S.heroPosted.length

      /* The hero's one-line reasoning strip. The section below streams the full
         argument; this only shows that there IS one, for whichever row the walk
         is on. First prose part of that row's entry, or the cited memory if the
         entry opens with one. */
      const M = taskMem()
      const mem = (id: string) => (M ? M.get(id) : null)
      const heroT = S.heroTid ? this.txn(S.heroTid) : null
      const heroE: StreamEntry | null = heroT
        ? (streamEntries().find(
            (x) => x.kind !== 'close' && (x.txns || []).indexOf(heroT.id) > -1,
          ) ?? null)
        : null
      if (heroE && heroT) {
        const parts = rparts(heroE)
        /* A batch entry covers one sentence across many rows. Reusing it row by
           row put the same line on screen seven times a pass, each time under a
           payee it did not describe. A routine row's honest reason is short and
           its own. */
        if ((heroE.txns || []).length > 2) {
          const acctNo = heroT.correctedTo || heroT.to || ''
          const acct = acctName(D, acctNo)
          /* THE ROW'S OWN citation first — never the entry's: a mixed batch
             (s306) cites a rule that covers one of its three rows, and taking
             the entry's first citation put the permits memory under the Puget
             power bill. Then the entry's evidence; then the client's own
             history, counted from the seed rather than asserted. */
          const mm = heroT.cites && heroT.cites.length ? mem(heroT.cites[0]) : null
          const ev = parts.find((x) => x.evidence && x.evidence.text)
          heroReason = mm ? 'Settled by a standing rule.' : 'Nothing here needs a judgment call.'
          if (mm && mm.body) {
            heroQuote = mm.body.replace(/\*/g, '')
          } else if (ev && ev.evidence) {
            /* the chip is already showing this evidence's label; show what it says */
            heroQuote = ev.evidence.text || ''
          } else if (heroT.payee) {
            const same = D.transactions.filter(
              (x) => x.payee === heroT.payee && x.id !== heroT.id && (x.correctedTo || x.to),
            )
            if (same.length && same.every((x) => (x.correctedTo || x.to) === acctNo)) {
              heroQuote =
                same.length +
                (same.length === 1 ? ' other charge' : ' other charges') +
                ' from ' +
                heroT.payee +
                ' in this client’s history, every one to ' +
                acct +
                '.'
            }
          }
          if (acct) heroTail = 'Coded to ' + acct + '.'
        } else {
          /* claim → the cited memory or evidence → conclusion, in stream order */
          let seenProse = 0
          for (let i = 0; i < parts.length && seenProse < 2; i++) {
            const p = parts[i]
            if (p.t) {
              if (seenProse === 0) heroReason = p.t
              else if (!heroTail) heroTail = p.t
              seenProse++
            } else if (p.cite && !heroQuote) {
              const mm = mem(p.cite)
              if (mm && mm.body) heroQuote = mm.body.replace(/\*/g, '')
            } else if (p.evidence && !heroQuote) {
              heroQuote = p.evidence.text || ''
            }
          }
          /* entries that open on the citation still need something in the claim slot */
          if (!heroReason && heroQuote) {
            heroReason = heroQuote
            heroQuote = ''
          }
        }
      }
      /* trimReason cuts at a sentence, else a clause, else a word — and drops a
         trailing colon, which would promise a quote that may have been trimmed
         away. 118 is deliberate: the strip is a precis, not the full argument. */
      heroReason = trimReason(heroReason, 118)
      heroQuote = trimReason(heroQuote, 118)
      heroTail = trimReason(heroTail, 118)

      const heroFlagN = heroList.filter((x) => x.state === 'needsyou').length
      /* CITATION MARKER, not the citation. The quoted memory body belongs to the
         section below, where it is revealed slowly; here it is one tag saying
         the judgment came from something written down rather than a guess. */
      if (heroE) {
        /* mirrors the quote's source, path by path: batch rows resolve their OWN
           citation (the entry's may belong to a sibling row), everything else
           the entry's. The chip and the quote must never disagree about where
           the judgment came from. */
        const isBatch = (heroE.txns || []).length > 2
        const rowMem =
          isBatch && heroT && heroT.cites && heroT.cites.length ? mem(heroT.cites[0]) : null
        const c = !isBatch ? rparts(heroE).find((p) => p.cite) : undefined
        if (rowMem) heroCite = scopeLabel(D, rowMem)
        else if (c && c.cite) heroCite = scopeLabel(D, mem(c.cite))
        else {
          const ev = rparts(heroE).find((p) => p.evidence)
          if (ev && ev.evidence) heroCite = ev.evidence.label || ''
        }
      }
      heroLabel = heroT && heroT.state === 'needsyou' ? 'Flagged for review' : 'Reasoning'
      heroSubj = heroT ? heroT.payee || heroT.raw : ''
      /* THE REST WINDOW. Between the end of a pass and the next restart there is
         no current row for ~3.3s, and the strip must not sit there as an empty
         labelled box. Resting state states the outcome instead, which is also
         the only place the pass is summed up. */
      if (!heroT || !heroReason) {
        heroQuote = ''
        heroTail = ''
        heroLabel = heroDone ? 'Pass complete' : 'Ready'
        heroSubj = D.client.short || D.client.name
        heroReason = heroDone
          ? heroDone +
            ' of ' +
            heroList.length +
            ' coded against this firm’s written rules and its own history. ' +
            'The remaining ' +
            heroFlagN +
            ' need a judgment nothing on file covers.'
          : heroList.length +
            ' entries landed in a holding account with nothing to categorize them by. ' +
            'Each one is read against the firm’s memory before it is posted.'
      }
      // counted the same way the rows are marked: only the flags the walk has reached
      const heroFlags = heroList.filter((t, i) => t.state === 'needsyou' && i <= S.heroAt).length
      heroCount = heroDone ? heroDone + ' posted' : D.transactions.length + ' items'
      heroFlagCount = heroFlags + ' flagged'
    }

    return (
      <div className="lgx-heropane" data-slot="landing-hero-pane">
        <div className="ts-pane ts-pane--list">
          <div className="ts-panehead">
            <span className="ts-kicker">Uncategorized Expense</span>
            <span className="ts-kicker ts-kicker--r">{heroCount}</span>
            <span className="ts-kicker lgx-kflag">{heroFlagCount}</span>
          </div>
          <div className="ts-rows">
            <div
              className="ts-rows-in"
              style={{ transform: 'translateY(-' + Math.round(heroShift) + 'px)' }}
            >
              {heroRows.map((r) => (
                <div key={r.id} className={r.cls}>
                  <span className="ts-date">{r.date}</span>
                  <span className="ts-raw">{r.raw}</span>
                  <span className="ts-dec">
                    <span className="ts-dchip">{r.chip}</span>
                  </span>
                  <span className="ts-amt">{r.amt}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* The argument behind the current row, in the shape it actually has:
            the claim, the written thing it rests on, then the conclusion. The
            tasks section streams the same three beats slowly; this is their
            compact echo. */}
        <div className="lgx-heroreason">
          <div className="lgx-hrhead">
            <span className="lgx-hrlabel">{heroLabel}</span>
            <span className="lgx-hrpayee">{heroSubj}</span>
            {heroCite ? <span className="lgx-hrcite">{heroCite}</span> : null}
          </div>
          <p className="lgx-hrtext">{heroReason}</p>
          {heroQuote ? <p className="lgx-hrquote">{heroQuote}</p> : null}
          {heroTail ? <p className="lgx-hrtail">{heroTail}</p> : null}
        </div>
      </div>
    )
  }
}
